<template>
  <div
    class="ring-timer"
    :class="{ 'is-overtime': isOvertime }"
    role="timer"
    aria-label="残り時間"
  >
    <svg
      class="ring-timer-svg"
      viewBox="0 0 160 160"
    >
      <defs>
        <linearGradient
          :id="gradientId"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop
            offset="0%"
            class="ring-timer-stop-start"
          />
          <stop
            offset="100%"
            class="ring-timer-stop-end"
          />
        </linearGradient>
      </defs>
      <circle
        class="ring-timer-bg"
        cx="80"
        cy="80"
        r="70"
      />
      <circle
        class="ring-timer-fg"
        cx="80"
        cy="80"
        r="70"
        :stroke="isOvertime ? null : `url(#${gradientId})`"
        :stroke-dasharray="CIRCUMFERENCE"
        :stroke-dashoffset="dashOffset"
      />
    </svg>
    <div class="ring-timer-inner">
      <div class="ring-timer-value">
        {{ remainingLabel }}
      </div>
      <div class="ring-timer-sub">
        {{ isOvertime ? '超過' : '残り時間' }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  ref,
  computed,
  onUnmounted,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  computeRemainingSeconds,
  computeRingRatio,
  formatRemainingLabel,
} from '@/views/composable/useRingTimer'

const CIRCUMFERENCE = 440

export default {
  props: {
    startAt: {
      type: String,
      default: null,
    },
    expireAt: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    const vm = getCurrentInstance()

    const now = ref(new Date())
    const intervalId = setInterval(() => {
      now.value = new Date()
    }, 1000)
    onUnmounted(() => clearInterval(intervalId))

    const remainingSeconds = computed(() => computeRemainingSeconds(props.expireAt, now.value))
    const isOvertime = computed(() => remainingSeconds.value !== null && remainingSeconds.value < 0)
    const remainingLabel = computed(() => formatRemainingLabel(remainingSeconds.value))
    const dashOffset = computed(() => {
      const ratio = computeRingRatio(props.startAt, props.expireAt, now.value)

      return CIRCUMFERENCE * (1 - ratio)
    })

    return {
      gradientId: `ring-timer-gradient-${vm.uid}`,
      CIRCUMFERENCE,
      remainingSeconds,
      isOvertime,
      remainingLabel,
      dashOffset,
    }
  },
}
</script>

<style lang="scss" scoped>
.ring-timer {
  width: 160px;
  height: 160px;
  position: relative;
  flex-shrink: 0;
}

.ring-timer-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.ring-timer-bg {
  fill: none;
  // トラック色はテーマ（Overview.vueのCSS変数）に追従。単体利用時はダーク前提の既定値
  stroke: var(--overview-ring-track, rgba(255, 255, 255, 0.08));
  stroke-width: 6;
}

// グラデーションもテーマ変数に追従（stop-color属性はvar()を解決しないためCSSで当てる）
.ring-timer-stop-start {
  stop-color: var(--overview-accent, #8b5cf6);
}

.ring-timer-stop-end {
  stop-color: var(--overview-emerald, #34d399);
}

.ring-timer-fg {
  fill: none;
  stroke-width: 6;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.5s ease;
}

// 超過時の色はテーマ変数に追従（stroke属性ではなくCSSで当てる。属性はvar()を解決しないため）
.is-overtime .ring-timer-fg {
  stroke: var(--overview-rose, #fb7185);
}

.ring-timer-inner {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ring-timer-value {
  font-size: 36px;
  font-weight: 900;
  letter-spacing: -2px;
  line-height: 1.1;
}

.is-overtime .ring-timer-value {
  color: var(--overview-rose, #fb7185);
}

.ring-timer-sub {
  font-size: 13px;
  opacity: 0.6;
  margin-top: 2px;
}
</style>
